import { LabelEnum, ValidationErrorMessagesEnum } from 'app/shared/enums';
import validateMinSelectedNo from 'app/validators/validateMinSelectedNo';
import validateCarLongRegistrationNumber from 'app/validators/validateCarLongRegistrationNumber';
import validateCarRegistrationNumber from 'app/validators/validateCarRegistrationNumber';
import validateOptionalCarRegistrationNumber from 'app/validators/validateOptionalCarRegistrationNumber';
import validateCarVin from 'app/validators/validateCarVin';
import validateDate from 'app/validators/validateDate';
import validateEmail from 'app/validators/validateEmail';
import validatePassword from 'app/validators/validatePassword';
import validatePesel from 'app/validators/validatePesel';
import validatePhoneNumber, {
  validatePhoneNumberIsAllowed,
} from 'app/validators/validatePhoneNumber';
import validateNipNumber from 'app/validators/validateNipNumber';
import validateWord from 'app/validators/validateWord';
import validateHouseNumber from 'app/validators/validateHouseNumber';
import validateIban from 'app/validators/validateIban';
import validateNumber from 'app/validators/validateNumber';
import validateStreetName from 'app/validators/validateStreetName';
import {
  validateMotorcycleVin,
  validateCepikDataCompatibility,
} from 'app/validators';
import { ValidatorDataInterface } from 'app/shared/interfaces/ValidatorData.interface';
import { agencyMode } from 'app/services/env.service';

const validationConfig = {
  CHECKBOX_GROUP: [
    {
      suffix: 'invalid_min_selected_no',
      content: LabelEnum.InvalidMinSelectedNo,
      validation: {
        validator: validateMinSelectedNo,
        params: ['minSelectedNo'],
      },
    },
  ],
  CAR_VIN: [
    {
      suffix: 'invalid_car_vin',
      content: LabelEnum.InvalidVin,
      validation: {
        validator: validateCarVin,
      },
    },
  ],
  MOTORCYCLE_VIN: [
    {
      suffix: 'invalid_motorcycle_vin',
      content: LabelEnum.InvalidVin,
      validation: {
        validator: validateMotorcycleVin,
      },
    },
  ],
  ZIP_CODE: [
    {
      suffix: 'invalid_zip_code',
      content: LabelEnum.InvalidZipCode,
    },
  ],
  EMAIL: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'invalid_email',
      content: LabelEnum.InvalidEmail,
      validation: {
        validator: validateEmail,
      },
    },
  ],
  PHONE_NUMBER: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'not_allowed_phone_number',
      content: ValidationErrorMessagesEnum.PhoneNumberNotAllowed,
      validation: {
        validator: validatePhoneNumberIsAllowed,
      },
    },
    {
      suffix: 'invalid_phone_number',
      content: ValidationErrorMessagesEnum.InvalidPhoneNumber,
      validation: {
        validator: validatePhoneNumber,
      },
    },
    {
      suffix: 'invalid_mobile_phone_number',
      content: ValidationErrorMessagesEnum.InvalidMobilePhoneNumber,
    },
    {
      suffix: 'invalid_mobile_phone_number_default',
      content: ValidationErrorMessagesEnum.InvalidPhoneNumber,
    },
    {
      suffix: 'backend_invalid_phone_number',
      content: ValidationErrorMessagesEnum.InvalidPhoneNumber,
    },
  ],
  NIP_NUMBER: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'invalid_nip_number',
      content: LabelEnum.InvalidNipNumber,
      validation: {
        validator: validateNipNumber,
      },
    },
  ],
  STREETNAME: [
    {
      suffix: 'invalid_street_name',
      content: LabelEnum.InvalidStreetName,
      validation: {
        validator: validateStreetName,
      },
    },
  ],
  HOUSE_NUMBER: [
    {
      suffix: 'invalid_house_number',
      content: LabelEnum.InvalidHouseNumber,
      validation: {
        validator: validateHouseNumber,
      },
    },
  ],
  WORD: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
  ],
  NAME: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'cepik_data_mismatch',
      content: LabelEnum.CepikDataMismatch,
      validation: {
        validator: validateCepikDataCompatibility,
        attachState: true,
      },
    },
  ],
  LASTNAME: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'cepik_data_mismatch',
      content: LabelEnum.CepikDataMismatch,
      validation: {
        validator: validateCepikDataCompatibility,
        attachState: true,
      },
    },
  ],
  CAR_REGISTRATION_NUMBER: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'invalid_car_registration_number',
      content: LabelEnum.InvalidCarRegistrationNumber,
      validation: {
        validator: validateCarRegistrationNumber,
      },
    },
  ],
  EUROBAND_CAR_REGISTRATION_NUMBER: [
    {
      suffix: 'invalid_word',
      content: LabelEnum.InvalidChar,
      validation: {
        validator: validateWord,
      },
    },
    {
      suffix: 'invalid_car_registration_number',
      content: LabelEnum.InvalidCarRegistrationNumber,
      validation: {
        validator: validateOptionalCarRegistrationNumber,
      },
    },
  ],
  CAR_LONG_REGISTRATION_NUMBER: [
    {
      suffix: 'invalid_car_registration_number',
      content: agencyMode
        ? LabelEnum.InvalidCarLongRegistrationNumberAgency
        : LabelEnum.InvalidCarLongRegistrationNumber,
      validation: {
        validator: validateCarLongRegistrationNumber,
      },
    },
  ],
  PASSWORD: [
    {
      suffix: 'invalid_password',
      content: LabelEnum.PasswordHint,
      validation: {
        validator: validatePassword,
      },
    },
  ],
  DATE: [
    {
      suffix: 'invalid_date',
      content: LabelEnum.InvalidDate,
      validation: {
        validator: (
          data: ValidatorDataInterface,
          minDate: string,
          maxDate: string
        ) => validateDate(minDate, maxDate, data),
        params: ['minDate', 'maxDate'],
        relatedSuffixes: ['too_young_owner'],
      },
    },
    {
      suffix: 'too_young_owner',
      content: LabelEnum.TooYoungOwner,
    },
  ],
  EXTENDED_DATE: [
    {
      suffix: 'invalid_date',
      content: LabelEnum.InvalidDate,
      validation: {
        validator: (
          data: ValidatorDataInterface,
          minDate: string,
          maxDate: string
        ) => validateDate(minDate, maxDate, data),
        params: ['minDate', 'maxDate'],
      },
    },
  ],
  PESEL: [
    {
      suffix: 'invalid_pesel',
      content: LabelEnum.InvalidPesel,
      validation: {
        validator: validatePesel,
        params: ['minAge'],
        relatedSuffixes: ['too_young_owner_pesel'],
      },
    },
    {
      suffix: 'too_young_owner_pesel',
      content: LabelEnum.TooYoungOwner,
    },
    {
      suffix: 'notUnique_pesel',
      content: LabelEnum.NotUniquePesel,
    },
    {
      suffix: 'notUnique_pesel_short',
      content: LabelEnum.NotUniquePeselShort,
    },
    {
      suffix: 'cepik_data_mismatch',
      content: LabelEnum.CepikDataMismatch,
      validation: {
        validator: validateCepikDataCompatibility,
        attachState: true,
      },
    },
  ],
  IBAN: [
    {
      suffix: 'invalid_iban',
      content: LabelEnum.InvalidIban,
      validation: {
        validator: validateIban,
      },
    },
  ],
  NUMBER: [
    {
      suffix: 'invalid_number',
      content: LabelEnum.InvalidNumber,
      validation: {
        validator: validateNumber,
        params: ['min', 'max'],
      },
    },
  ],
};
export default validationConfig;
